import { ListRenderItem } from '@shopify/flash-list';
import { Card } from 'components/Card';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useConfirm } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { difference, intersection, isEqual, without } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL, NAVIGATE_MODAL_BACK } from 'store/actions';
import {
  doAseFieldInstanceAddManyExisting,
  doAseSetGroupFilter,
} from 'store/assessments/actions';
import {
  selectAseTplFieldGroup,
  selectAseFieldGroupStats,
  selectAseFieldInstanceIdsToAddForGroupFilteredAndSorted,
  selectAseFieldInstancesCountForGroup,
  selectAseTplFieldToGroup,
  selectAseFieldGroupFilter,
} from 'store/assessments/selectors';
import { IconButton } from 'components/IconButton';
import { ListSelectAllCheckbox } from 'components/ListSelectAllCheckbox';
import { RepeatableListItem } from './RepeatableListItem';
import { List } from 'components/List';
import { PageHeader } from 'components/PageHeader';
import { PageToolbar } from 'components/PageToolbar';

type Props = ModalScreenProps<ModalScreens.ASEREPEATABLEINSTANCENEWFROMLIST>;

export const AddRepeatableListScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const { groupId, parent } = route.params;
  const [selected, setSelected] = React.useState<string[]>([]);

  const filterKey = 'adding';
  const groupFilterKey = `${filterKey}-${groupId}`;
  const groupFilter = useSelector((state) =>
    selectAseFieldGroupFilter(state, groupFilterKey),
  );
  const searchValue = groupFilter.search;

  const group = useSelector((state) => selectAseTplFieldGroup(state, groupId));
  const parentGroup = useSelector((state) =>
    selectAseTplFieldToGroup(state, parent?.fieldId || '-1'),
  );

  // Get total number of instances in asset and in assessment
  const totalInstanceCount = useSelector((state) =>
    selectAseFieldInstancesCountForGroup(state, {
      groupId,
      parent: null,
      inAssessmentOnly: false,
    }),
  );

  const groupStats = useSelector((state) =>
    selectAseFieldGroupStats(state, {
      groupId,
      parent,
      inAssessmentOnly: true,
    }),
  );

  const instanceIds = useSelector((state) =>
    selectAseFieldInstanceIdsToAddForGroupFilteredAndSorted(state, {
      groupId,
      parent,
      inAssessmentOnly: false,
    }),
  );

  const filteredInstanceIds = useSelector((state) =>
    selectAseFieldInstanceIdsToAddForGroupFilteredAndSorted(state, {
      groupId,
      parent,
      inAssessmentOnly: false,
      filtered: true,
    }),
  );

  const handlePressAdd = () => {
    dispatch(NAVIGATE_MODAL_BACK());
    dispatch(
      doAseFieldInstanceAddManyExisting({
        instanceIds: selected,
        parent,
      }),
    );
  };

  const unaddedCount = totalInstanceCount - groupStats.assessmentInstancesCount;

  const handlePressItem = React.useCallback(
    (instanceId: string) =>
      setSelected((current) =>
        current.includes(instanceId)
          ? without(current, instanceId)
          : [...current, instanceId],
      ),
    [],
  );

  const renderItem: ListRenderItem<string> = React.useCallback(
    ({ item, extraData }) => {
      if (item === 'existingend') {
        return (
          <Card
            explanation={
              parent
                ? pluralise(
                    unaddedCount,
                    `This ${group?.calculatedInfo.singularName.toLowerCase()} has not been linked to this ${parentGroup?.calculatedInfo.singularName.toLowerCase()} yet`,
                    `These ${group?.name.toLowerCase()} have not been linked to this ${parentGroup?.calculatedInfo.singularName.toLowerCase()} yet`,
                  )
                : pluralise(
                    unaddedCount,
                    `This ${group?.calculatedInfo.singularName.toLowerCase()} exists on the asset but has not been added to the assessment yet`,
                    `These ${group?.name.toLowerCase()} exist on the asset but have not been added to the assessment yet`,
                  )
            }
          />
        );
      }
      return (
        <RepeatableListItem
          instanceId={item}
          onPress={handlePressItem}
          checkedValue={selected.includes(item)}
          parent={parent}
          disableAddButton={!!extraData.listHasSelection}
          from="add"
        />
      );
    },
    [
      handlePressItem,
      selected,
      parent,
      unaddedCount,
      group?.calculatedInfo.singularName,
      group?.name,
      parentGroup?.calculatedInfo.singularName,
    ],
  );

  const handleClose = React.useCallback(() => {
    if (selected.length) {
      confirm({
        text: `The chosen ${pluralise(
          selected.length,
          group?.calculatedInfo.singularName.toLowerCase() || 'item',
          group?.name.toLowerCase() || 'items',
        )} will not be added`,
        confirmText: 'Okay',
        cancelText: 'Cancel',
        destructive: true,
        onConfirm: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [
    confirm,
    dispatch,
    group?.calculatedInfo.singularName,
    group?.name,
    selected.length,
  ]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !selected.length });
  }, [navigation, selected.length]);
  useEsc(handleClose);

  React.useEffect(() => {
    if (!unaddedCount) {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  }, [dispatch, unaddedCount]);

  React.useEffect(() => {
    const newSelected = difference(selected, groupStats.assessmentInstanceIds);
    if (!isEqual(selected, newSelected)) {
      setSelected(newSelected);
    }
  }, [groupStats.assessmentInstanceIds, selected]);

  const handleSearch = React.useCallback(
    (search: string) => {
      dispatch(
        doAseSetGroupFilter({
          groupId: groupFilterKey,
          data: { ...groupFilter, search },
        }),
      );
    },
    [dispatch, groupFilter, groupFilterKey],
  );

  const finalInstanceIds = intersection(instanceIds, [
    ...filteredInstanceIds,
    ...selected,
  ]);

  if (!group) {
    return null;
  }

  const hasEnabledFilterOptions =
    !!Object.keys(groupFilter.checks).length ||
    !!groupFilter.availabilityCategories?.length;

  return (
    <Page
      header={
        <>
          <PageHeader
            title={`${pluralise(
              finalInstanceIds.length,
              `1 ${group.calculatedInfo.singularName}`,
              `{x} ${group.name}`,
            )}${searchValue ? ' (Filtered)' : ''}`}
            subtitle={
              selected.length
                ? `${selected.length} Selected`
                : parentGroup
                  ? `Add to Assessment and Link to ${parentGroup.calculatedInfo.singularName}`
                  : 'Add to Assessment'
            }
            onPressCancelButton={handleClose}
            onPressSaveButton={selected.length ? handlePressAdd : null}
          />
          <PageToolbar
            onSearch={handleSearch}
            searchValue={searchValue}
            searchPlaceholder={`Search ${group.name}`}
            onSearchEsc={handleClose}
            leftChildren={
              <ListSelectAllCheckbox
                onSelectAll={() => setSelected(finalInstanceIds)}
                onSelectNone={() => setSelected([])}
                selectionState={
                  selected.length
                    ? selected.length !== finalInstanceIds.length
                      ? 'some'
                      : 'all'
                    : 'none'
                }
              />
            }
          >
            <IconButton
              icon={ICONS.SlidersUpLight}
              onPress={() =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.AseFieldInstancesOptions,
                    params: { groupId, listType: 'adding' },
                  }),
                )
              }
              showBadge={hasEnabledFilterOptions}
            />
          </PageToolbar>
        </>
      }
    >
      <List
        data={[...finalInstanceIds, 'existingend']}
        extraData={{ listHasSelection: !!selected.length }}
        keyExtractor={(instanceId) => instanceId}
        renderItem={renderItem}
      />
    </Page>
  );
};
